// LLM generated - Model Claude 4 Sonnet
import { logError } from '~/common/utils/log';
import { registerChannelAuthHandler } from '~/config/initPusher';
import {
  authenticateUser as authenticateUserService,
  authenticateUserSubscriptions as authenticateUserSubscriptionsService,
} from '../live-activity-service.service.api';
import type { PusherUserAuthResponse, PusherUserAuthSubscriptionsResponse } from '../types';

// Internal type for cached auth result with socketId
interface CachedUserAuthResponse extends PusherUserAuthResponse {
  socketId: string;
}

// Global variable to store user auth result (per connection)
// Only used when subscribing to private-/presence- channels
let userAuthResult: CachedUserAuthResponse | null = null;
let isUserAuthenticating = false;

/**
 * Authenticate user with live-activity service (Step 1)
 * Cached per socket connection to avoid redundant auth calls
 */
const authenticateUser = async (socketId: string): Promise<CachedUserAuthResponse> => {
  // If we have a cached result but for a different socketId, clear the cache
  // This handles reconnection scenarios where Pusher assigns a new socket ID
  if (userAuthResult && userAuthResult.socketId !== socketId) {
    userAuthResult = null;
  }

  // Return cached auth if available for this socket
  if (userAuthResult && userAuthResult.socketId === socketId) {
    return userAuthResult;
  }

  // If another request is already authenticating, wait for it
  if (isUserAuthenticating) {
    // Wait for the other request to complete (up to 5 seconds)
    const maxWaitTime = 5000;
    const pollInterval = 100;
    const maxAttempts = maxWaitTime / pollInterval;

    for (let attempts = 0; attempts < maxAttempts; attempts += 1) {
      if (!isUserAuthenticating) break;
      try {
        // eslint-disable-next-line no-await-in-loop
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), pollInterval);
        });
      } catch (error) {
        logError('Error while waiting for authentication:', {
          socketId,
          error: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString(),
        });
      }
    }

    // Check if the other request succeeded
    if (userAuthResult && userAuthResult.socketId === socketId) {
      return userAuthResult;
    }
  }

  // Mark that we're authenticating
  isUserAuthenticating = true;

  try {
    const authData = await authenticateUserService({ socket_id: socketId });
    userAuthResult = { ...authData, socketId };
    return userAuthResult;
  } finally {
    isUserAuthenticating = false;
  }
};

/**
 * Authenticate specific channel subscription (Step 2)
 */
const authenticateChannelSubscription = async (
  socketId: string,
  channelName: string
): Promise<PusherUserAuthSubscriptionsResponse> => {
  try {
    return await authenticateUserSubscriptionsService({
      socket_id: socketId,
      channel_name: channelName,
    });
  } catch (error) {
    logError('Pusher channel auth error:', error, {
      channel: channelName,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

/**
 * Two-step authentication handler for live-activity service channels
 * Step 1: Authenticate user (once per connection)
 * Step 2: Authorize specific channel subscription
 */
const liveActivityAuthHandler = (
  socketId: string,
  channelName: string,
  callback: (error: Error | null, authInfo?: PusherUserAuthSubscriptionsResponse) => void
): void => {
  (async () => {
    try {
      // Step 1: Ensure user is authenticated (only once per connection)
      await authenticateUser(socketId);

      // Step 2: Authenticate the specific channel subscription
      const subscriptionAuthData = await authenticateChannelSubscription(socketId, channelName);

      // Success - return auth data to Pusher
      callback(null, subscriptionAuthData);
    } catch (error) {
      // Error - log and return error to Pusher
      logError('Pusher auth failed:', error, {
        channel: channelName,
        timestamp: new Date().toISOString(),
      });
      callback(error instanceof Error ? error : new Error(String(error)));
    }
  })();
};

/**
 * Clear user auth cache (for token refresh scenarios or connection changes)
 */
export const clearLiveActivityAuthCache = (): void => {
  userAuthResult = null;
  isUserAuthenticating = false;
};

// Track if handler has been registered to prevent duplicate registration
let isHandlerRegistered = false;

/**
 * Register live-activity auth handler for channels matching patterns:
 * - presence-@contest_*
 * - presence-@chat_*
 * - private-@contest_*
 * - private-@chat_*
 *
 * This function is idempotent - safe to call multiple times.
 * The handler will only be registered once.
 */
export const registerLiveActivityAuthHandler = (): void => {
  // Guard against duplicate registration
  if (isHandlerRegistered) {
    return;
  }

  // Match any presence- or private- channel with "@contest" or "@chat" in the name
  // Pattern handles formats like: private-@chat_uuid_reactions, presence-@contest_uuid_messages
  const liveActivityChannelPattern = /^(presence-|private-)@(contest|chat)_/;

  registerChannelAuthHandler(liveActivityChannelPattern, liveActivityAuthHandler);
  isHandlerRegistered = true;
};
